import { cx } from "@suse-o11y/class-variance";
import { isPresent } from "@suse-o11y/is-present";
import type { NativeButtonProps } from "antd/es/button/button";
import omit from "lodash/omit";
import pickBy from "lodash/pickBy";
import React from "react";
import type { BaseSchema } from "yup";

import type { Node, NodeId } from "~/models/dto";

import { LockedNodeModalMessage } from "../locked-node-modal/LockedNodeModalMessage";
import { Modal } from "../modal/Modal";
import { getModalWidth } from "../modal/modal-size";

const modalLock = (nodeType: string): Promise<boolean> =>
	new Promise<boolean>((resolve) => {
		Modal.confirm({
			className: cx(
				"[&_.ant-modal-confirm-content]:m-0",
				"[&_.ant-modal-confirm-btns_.ant-btn-default]:float-left [&_.ant-modal-confirm-btns_.ant-btn-default]:ml-0",
				"[&_.ant-modal-confirm-btns_.ant-btn-primary]:ml-2",
			),
			icon: " ",
			content: <LockedNodeModalMessage nodeType={nodeType} />,
			okText: "PROCEED ANYWAY",
			okType: "default",
			onOk: () => void resolve(true),

			autoFocusButton: "cancel",

			cancelText: "CANCEL",
			cancelButtonProps: {
				type: "primary",
			} as NativeButtonProps,
			onCancel: () => void resolve(false),
			centered: true,
			width: getModalWidth("medium-small"),
			zIndex: 1005,
		});
	});

const unlockNode = <N extends Node>(node: N): N => omit(node, "ownedBy") as N;

class HandleLockNodeError extends Error {}
HandleLockNodeError.prototype.name = "HandleLockNodeError";

export const handleLockNode = async <N extends Node>(node: N): Promise<N | HandleLockNodeError> => {
	if (await modalLock(node._type)) {
		return unlockNode(node);
	}
	return Promise.resolve(new HandleLockNodeError("Node was already unlocked"));
};

export const COMPONENT_ACTION_NAME = "Component action";

export const getComponentWithName = (name?: string) =>
	name ? `${COMPONENT_ACTION_NAME} "${name}"` : COMPONENT_ACTION_NAME;

export const getNodeTypeWithName = (userFriendlyNodeTypeName: string, name?: string): string =>
	name ? `${userFriendlyNodeTypeName} "${name}"` : userFriendlyNodeTypeName;

export const buildElement = <T extends Node>({
	schema,
	type,
	id,
	values,
}: {
	schema: BaseSchema<Partial<T>>;
	values: Partial<T>;
	type?: T["_type"];
	id?: NodeId;
}): T => ({
	...(pickBy(schema.cast(values, { stripUnknown: true }), isPresent) as unknown as T),
	id,
	_type: values._type || type,
});
